// ═══════════════════════════════════════════════════════════════════════════
// ShiftTemplateManager — CRUD for shift templates (built-in + custom)
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  SEGMENT_TYPES, BLOCK_TYPES, BUILT_IN_TEMPLATES, SECTOR_PRESETS,
  createSegment,
} from '../utils/shiftTypes';
import { AUDIT_ACTIONS, logTemplateEvent } from '../utils/auditLog';

const SEGMENT_COLOURS = {
  [SEGMENT_TYPES.STANDARD]: 'bg-blue-600',
  [SEGMENT_TYPES.SLEEP_IN]: 'bg-indigo-500',
  [SEGMENT_TYPES.ON_CALL]: 'bg-amber-500',
  [SEGMENT_TYPES.BREAK]: 'bg-gray-400',
};

const SEGMENT_LABELS = {
  [SEGMENT_TYPES.STANDARD]: 'Standard',
  [SEGMENT_TYPES.SLEEP_IN]: 'Sleep-in',
  [SEGMENT_TYPES.ON_CALL]: 'On-call',
  [SEGMENT_TYPES.BREAK]: 'Break',
};

export default function ShiftTemplateManager({ companyId, userId, userName }) {
  const { t } = useTranslation();
  const [customTemplates, setCustomTemplates] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | template object
  const [form, setForm] = useState({
    name: '',
    sector: 'general',
    blockType: BLOCK_TYPES.SINGLE,
    segments: [createSegment(SEGMENT_TYPES.STANDARD, '09:00', '17:00')],
    description: '',
  });

  // Firestore listener for custom templates
  useEffect(() => {
    if (!companyId) return;
    const unsub = onSnapshot(
      collection(db, 'companies', companyId, 'shiftTemplates'),
      snap => setCustomTemplates(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, [companyId]);

  // ── Helpers ──
  const resetForm = () => {
    setForm({
      name: '',
      sector: 'general',
      blockType: BLOCK_TYPES.SINGLE,
      segments: [createSegment(SEGMENT_TYPES.STANDARD, '09:00', '17:00')],
      description: '',
    });
    setEditing(null);
  };

  const startEdit = (template) => {
    setForm({
      name: template.name,
      sector: template.sector || 'general',
      blockType: template.blockType || BLOCK_TYPES.SINGLE,
      segments: template.segments.map(s => ({ ...s })),
      description: template.description || '',
    });
    setEditing(template);
  };

  const addSegment = () => {
    const last = form.segments[form.segments.length - 1];
    setForm({
      ...form,
      segments: [
        ...form.segments,
        createSegment(SEGMENT_TYPES.STANDARD, last?.endTime || '09:00', '17:00'),
      ],
    });
  };

  const updateSegment = (idx, field, value) => {
    const segs = [...form.segments];
    segs[idx] = { ...segs[idx], [field]: value };
    setForm({ ...form, segments: segs });
  };

  const removeSegment = (idx) => {
    if (form.segments.length <= 1) return;
    setForm({ ...form, segments: form.segments.filter((_, i) => i !== idx) });
  };

  // ── Save ──
  const handleSave = async () => {
    const data = {
      name: form.name.trim(),
      sector: form.sector,
      blockType: form.blockType,
      segments: form.segments,
      description: form.description.trim(),
      updatedAt: new Date().toISOString(),
    };

    try {
      if (editing && editing.id) {
        await updateDoc(doc(db, 'companies', companyId, 'shiftTemplates', editing.id), data);
        await logTemplateEvent(companyId, AUDIT_ACTIONS.TEMPLATE_UPDATED, {
          userId, userName, templateId: editing.id, templateName: data.name,
          sector: data.sector, segmentCount: data.segments.length,
        });
      } else {
        data.createdAt = new Date().toISOString();
        data.createdBy = userId;
        const ref = await addDoc(collection(db, 'companies', companyId, 'shiftTemplates'), data);
        await logTemplateEvent(companyId, AUDIT_ACTIONS.TEMPLATE_CREATED, {
          userId, userName, templateId: ref.id, templateName: data.name,
          sector: data.sector, segmentCount: data.segments.length,
        });
      }
      resetForm();
    } catch (err) {
      console.error('Error saving template:', err);
    }
  };

  const handleDelete = async (template) => {
    if (!window.confirm(t('templates.confirmDelete', 'Delete this template?'))) return;
    try {
      await deleteDoc(doc(db, 'companies', companyId, 'shiftTemplates', template.id));
      await logTemplateEvent(companyId, AUDIT_ACTIONS.TEMPLATE_DELETED, {
        userId, userName, templateId: template.id, templateName: template.name,
        sector: template.sector, segmentCount: template.segments?.length || 0,
      });
    } catch (err) {
      console.error('Error deleting template:', err);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-white">{t('templates.title', 'Shift Templates')}</h3>
        <button onClick={() => setEditing('new')}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium">
          + {t('templates.create', 'New Template')}
        </button>
      </div>

      {/* Custom Templates */}
      {customTemplates.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-400 mb-2">{t('templates.custom', 'Custom Templates')}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {customTemplates.map(tmpl => (
              <div key={tmpl.id} className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="font-medium text-white">{tmpl.name}</div>
                    <div className="text-xs text-gray-400 capitalize">{tmpl.sector || 'General'} · {tmpl.blockType}</div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => startEdit(tmpl)} className="text-blue-400 hover:text-blue-300 text-xs">✏️</button>
                    <button onClick={() => handleDelete(tmpl)} className="text-red-400 hover:text-red-300 text-xs">🗑️</button>
                  </div>
                </div>
                {/* Mini timeline */}
                <div className="flex h-2 rounded overflow-hidden bg-gray-900 mb-1">
                  {(tmpl.segments || []).map((seg, j) => (
                    <div key={j} className={`${SEGMENT_COLOURS[seg.type]} h-full flex-1`} />
                  ))}
                </div>
                <div className="text-xs text-gray-500">
                  {(tmpl.segments || []).length} segments · {tmpl.description || ''}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Built-in Templates */}
      <div>
        <h4 className="text-sm font-semibold text-gray-400 mb-2">{t('templates.builtIn', 'Built-in Templates')}</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {BUILT_IN_TEMPLATES.map((tmpl, i) => (
            <div key={i} className="bg-gray-800/50 border border-gray-700/50 rounded-lg p-4">
              <div className="font-medium text-gray-300">{tmpl.name}</div>
              <div className="text-xs text-gray-500 capitalize mb-2">{tmpl.sector} · {tmpl.blockType}</div>
              <div className="flex h-2 rounded overflow-hidden bg-gray-900">
                {tmpl.segments.map((seg, j) => (
                  <div key={j} className={`${SEGMENT_COLOURS[seg.type]} h-full flex-1`} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Editor Modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl shadow-2xl w-full max-w-xl max-h-[85vh] overflow-y-auto border border-gray-700">
            <div className="flex items-center justify-between p-5 border-b border-gray-700">
              <h3 className="text-lg font-bold text-white">
                {editing === 'new' ? t('templates.createTitle', 'Create Template') : t('templates.editTitle', 'Edit Template')}
              </h3>
              <button onClick={resetForm} className="text-gray-400 hover:text-white text-xl">&times;</button>
            </div>

            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t('templates.name', 'Name')}</label>
                  <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm" />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t('templates.sector', 'Sector')}</label>
                  <select value={form.sector} onChange={e => setForm({ ...form, sector: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm">
                    {Object.keys(SECTOR_PRESETS).map(s => (
                      <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">{t('templates.blockType', 'Block Type')}</label>
                <select value={form.blockType} onChange={e => setForm({ ...form, blockType: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm">
                  <option value={BLOCK_TYPES.SINGLE}>Single</option>
                  <option value={BLOCK_TYPES.LINKED}>Linked (Multi-part)</option>
                  <option value={BLOCK_TYPES.SPLIT}>Split Shift</option>
                </select>
              </div>

              {/* Segments */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-300">{t('templates.segments', 'Segments')}</label>
                  <button onClick={addSegment} className="text-xs text-blue-400 hover:text-blue-300">+ Add</button>
                </div>
                <div className="space-y-2">
                  {form.segments.map((seg, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 bg-gray-700/60 rounded-lg">
                      <div className={`w-2 h-8 rounded ${SEGMENT_COLOURS[seg.type]}`} />
                      <select value={seg.type} onChange={e => updateSegment(i, 'type', e.target.value)}
                        className="bg-gray-700 border border-gray-600 text-white text-xs rounded px-2 py-1 w-24">
                        {Object.entries(SEGMENT_LABELS).map(([v, l]) => (
                          <option key={v} value={v}>{l}</option>
                        ))}
                      </select>
                      <input type="time" value={seg.startTime} onChange={e => updateSegment(i, 'startTime', e.target.value)}
                        className="bg-gray-700 border border-gray-600 text-white text-xs rounded px-2 py-1 w-24" />
                      <span className="text-gray-500 text-xs">→</span>
                      <input type="time" value={seg.endTime} onChange={e => updateSegment(i, 'endTime', e.target.value)}
                        className="bg-gray-700 border border-gray-600 text-white text-xs rounded px-2 py-1 w-24" />
                      <button onClick={() => removeSegment(i)} disabled={form.segments.length <= 1}
                        className="text-red-400 hover:text-red-300 text-xs disabled:opacity-30">✕</button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">{t('templates.description', 'Description')}</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  rows={2} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm" />
              </div>
            </div>

            <div className="flex justify-end gap-3 p-5 border-t border-gray-700">
              <button onClick={resetForm} className="px-4 py-2 text-gray-400 hover:text-white text-sm">
                {t('common.cancel', 'Cancel')}
              </button>
              <button onClick={handleSave} disabled={!form.name.trim()}
                className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-lg text-sm font-medium">
                {t('common.save', 'Save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
